import { useState, useEffect, useCallback, useRef } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const STORAGE_KEYS = {
  installed: "pwa_installed",
  dismissedAt: "pwa_dismissed_at",
  sessionStart: "pwa_session_start",
  navCount: "pwa_nav_count",
};

const COOLDOWN_DAYS = 7;
const ELIGIBILITY_SECONDS = 30;
const ELIGIBILITY_NAVS = 2;

function isStandaloneMode(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
}

function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

function isDismissCoolingDown(): boolean {
  const ts = localStorage.getItem(STORAGE_KEYS.dismissedAt);
  if (!ts) return false;
  const diff = Date.now() - Number(ts);
  return diff < COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
}

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.installed) === "true" || isStandaloneMode();
  });
  const [isStandalone] = useState(isStandaloneMode);
  const [isIOS] = useState(isIOSSafari);
  const [eligible, setEligible] = useState(false);
  const eligibilityChecked = useRef(false);

  // Track session start + nav count for eligibility
  useEffect(() => {
    if (isInstalled || isStandalone) return;

    if (!sessionStorage.getItem(STORAGE_KEYS.sessionStart)) {
      sessionStorage.setItem(STORAGE_KEYS.sessionStart, String(Date.now()));
      sessionStorage.setItem(STORAGE_KEYS.navCount, "1");
    } else {
      const count = Number(sessionStorage.getItem(STORAGE_KEYS.navCount) || "0") + 1;
      sessionStorage.setItem(STORAGE_KEYS.navCount, String(count));
    }
  }, [isInstalled, isStandalone]);

  // Eligibility timer
  useEffect(() => {
    if (isInstalled || isStandalone || isDismissCoolingDown()) return;

    const check = () => {
      const start = Number(sessionStorage.getItem(STORAGE_KEYS.sessionStart) || Date.now());
      const elapsed = (Date.now() - start) / 1000;
      const navs = Number(sessionStorage.getItem(STORAGE_KEYS.navCount) || "0");
      if (elapsed >= ELIGIBILITY_SECONDS || navs >= ELIGIBILITY_NAVS) {
        setEligible(true);
        eligibilityChecked.current = true;
      }
    };

    check();
    if (!eligibilityChecked.current) {
      const interval = setInterval(check, 5000);
      return () => clearInterval(interval);
    }
  }, [isInstalled, isStandalone]);

  // beforeinstallprompt (Android/Chrome/Edge)
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      localStorage.setItem(STORAGE_KEYS.installed, "true");
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const showInstallPrompt = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem(STORAGE_KEYS.installed, "true");
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const dismissInstall = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.dismissedAt, String(Date.now()));
    setDeferredPrompt(null);
    setEligible(false);
  }, []);

  const canInstall = !!deferredPrompt && !isInstalled && !isStandalone;
  const showIOSGuide = isIOS && !isInstalled && !isStandalone && eligible && !isDismissCoolingDown();
  const showOpenInApp = isInstalled && !isStandalone;

  return {
    canInstall,
    isInstalled,
    isStandalone,
    isIOS,
    eligible,
    showIOSGuide,
    showOpenInApp,
    showInstallPrompt,
    dismissInstall,
  };
}
